/**
 * PRODUCT_DEV-891 パスワード再設定の「誰に対して・どう案内するか」を決める純粋関数。
 *
 * 画面（ForgotPasswordUI / ResetPasswordUI）とハンドラ（useLoginHandlers）が同じ判断を
 * 使うのでここに集める。`@/` の別名を使わない（ルートの vitest から素のまま読めるように）。
 *
 * ## 決めたこと（2026-09-13 ユーザ確認）
 *
 * - **テナント専用の画面は作らない。** 全テナント共通で「メールアドレスまたは〈ID の呼び方〉」を受ける
 * - 独自ID が見つからなければ、入力値をメールアドレス（ログインID）として扱う
 * - メールアドレスが登録されていない人と、**ID が見つからない人に同じ案内を出す**
 *   （片方だけ別の文言にすると、ID を1つずつ試して在籍者を割り出せる）
 * - 管理者への「依頼する」ボタンは作らない（運用が決まってから）
 */

/** 入力欄の呼び方。テナントが呼び方（例「行員番号」）を持っていればそれを使う。 */
export const buildResetIdLabel = (
  loginIdLabel: string | null,
  isAliasMode: boolean,
): string =>
  `メールアドレスまたは${loginIdLabel ?? (isAliasMode ? "エイリアス" : "ログインID")}`;

/**
 * 再設定コードを届けられないときの案内。
 *
 * **「見つからない」と「メールが無い」を区別しない**文言にしてある（上の決定）。
 * 「登録がないため」ではなく「登録が確認できないため」なのはそのため。
 */
export const ADMIN_REISSUE_MESSAGE =
  "メールアドレスの登録が確認できないため、管理者にパスワードの再発行を依頼してください。";

/**
 * 再設定コードを送る先が無い、という Cognito のエラーか。
 *
 * - `UserNotFoundException`: そのユーザー名が無い（アプリクライアントの
 *   PreventUserExistenceErrors が LEGACY のため、存在しないと明示的に返る）
 * - `InvalidParameterException` の "no registered/verified email or phone_number":
 *   ユーザーはいるがメールが無い（とちぎんの行員の大半がこれ）
 *
 * `InvalidParameterException` を名前だけで拾わない。別の入力不備まで
 * 「管理者に依頼して」になり、原因が読めなくなる。
 */
export const isNoResetDestinationError = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const { name = "", message = "" } = err as { name?: unknown; message?: unknown };
  const n = typeof name === "string" ? name : "";
  const m = typeof message === "string" ? message : "";
  if (n === "UserNotFoundException") return true;
  if (/Username\/client id combination not found/i.test(m)) return true;
  if (/no registered\/verified email or phone.?number/i.test(m)) return true;
  return false;
};

/**
 * Cognito に渡すユーザー名。独自ID が解決できればその Cognito ユーザー名、
 * できなければ入力値をそのまま使う（メールアドレス／ログインID として扱う）。
 *
 * ⚠ コード確認（confirmResetPassword）でも**同じ値**を使うこと。画面に出ている
 * 入力値（独自ID）を渡すと Cognito がユーザーを引けず、正しいコードでも失敗する。
 */
export const pickResetUsername = (
  typed: string,
  resolved: { resolved: boolean; cognitoUsername: string } | null,
): string => (resolved?.resolved ? resolved.cognitoUsername : typed);
